import React, { useEffect, useRef, useState } from 'react';
import { Wizard, Box, Button, Modal, Alert, SpaceBetween } from '@amzn/awsui-components-console';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { GetWorkflowDefinitionResponse, StartBulkWorkflowExecutionsResponse, FailedEntity, WorkflowDefinition } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { SNOW_NXT_WORKFLOW_ENDPOINT, SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { AuthorizeAPIResponse } from 'src/@types/SNOWDataServiceTypes';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router';
import { KeyStringValuePairsObject } from 'src/@types/common';
import { useAuthContext } from 'src/providers/AuthContext';
import ErrorLoader from 'src/components/commonComponents/errorLoader/errorLoader';
import { useHistory, useLocation } from 'react-router-dom';
import BulkExecutionDownloadTemplateStep from 'src/components/selfServeWorkflows/requirementsPortal/bulkUploadSteps/BulkExecutionDownloadTemplateStep';
import BulkExecutionReviewStep from 'src/components/selfServeWorkflows/requirementsPortal/bulkUploadSteps/BulkExecutionReviewStep';
import BulkExecutionUploadInputStep from './BulKExecutionUploadInputStep';
import { logger } from 'src/utils/logger';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { getAwsCredentials } from 'src/authentication/cognito';
import AWS from 'aws-sdk';

export type BulkEntityValidationRecord = {
	entityId: string;
	entityType: string;
	workflowId?: string;
	bulkExecutionId?: string;
	validationStatus?: string;
	validationFailureMessage?: string;
	validationExecutionId?: string;
	validationResults?: unknown;
	validationReport?: unknown;
	inputRecord: Record<string, unknown>;
	autoPopulationResults?: Record<string, unknown>;
	autoPopulated?: boolean;
};

const pluralizeEntity = (count: number) => `${count} ${count === 1 ? 'entity' : 'entities'}`;

const BulkUploadRequirementStepProgress = () => {
	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const [bulkId, setBulkId] = useState('');
	const [selectedList, setSelectedList] = useState<BulkEntityValidationRecord[]>([]);
	const [showLaunchModal, setShowLaunchModal] = useState(false);
	const [fetchedRecords, setFetchedRecords] = useState<BulkEntityValidationRecord[]>([]);
	const [redirectUrl, setRedirectUrl] = useState('');
	const [onCancel, setOnCancel] = useState(false);
	const [showValidationError, setShowValidationError] = useState(false);
	const [failedEntities, setFailedEntities] = useState<FailedEntity[]>([]);
	const [succeededCount, setSucceededCount] = useState(0);
	const [showPartialFailureModal, setShowPartialFailureModal] = useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [retryCount, setRetryCount] = useState(0);
	const MAX_RETRIES = 3;

	const urlLocation = useLocation();
	const searchParams = new URLSearchParams(urlLocation.search);
	const bulkExecutionId = searchParams.get('bulkExecutionId');

	const history = useHistory();
	const timeOutRef = useRef<ReturnType<typeof setTimeout>>();

	const handleOnSubmit = () => {
		setShowLaunchModal(!showLaunchModal);
	};

	const { workflowType } = useParams<KeyStringValuePairsObject>();
	const { userName } = useAuthContext();

	const {
		data: authResponse,
	} = useFetchForSDS<AuthorizeAPIResponse>({
		path: '/authorize',
		method: 'POST',
		body: {
			user: userName,
			resource: {
				...OLAF_WORKFLOW_BINDLE_DATA,
				ids: [workflowType],
			},
			requestId: uuidv4(),
		},
	});

	useEffect(() => {
		if (bulkExecutionId) {
			setBulkId(bulkExecutionId);
			if (activeStepIndex === 0) {
				setActiveStepIndex(1);
			}
		} else {
			logger.info('No bulkExecutionId found in the query parameters.');
		}
	}, [bulkExecutionId]);

	const createEntityAndStartWorkflow = async (
		selectedEntities: BulkEntityValidationRecord[],
		batchExecutionRequired: boolean
	) => {
		setShowLaunchModal(false);

		// Update selected entities to READY status before starting workflow
		await getAwsCredentials();
		const credentials = {
			accessKey: AWS.config.credentials?.accessKeyId,
			secretKey: AWS.config.credentials?.secretAccessKey,
			sessionToken: AWS.config.credentials?.sessionToken,
		};

		try {
			await Promise.all(
				selectedEntities.map(async (record) => {
					const updatePath = `/entity/${workflowType}/${record.entityId}`;
					
					await callSNOWDataService({
						path: updatePath,
						method: 'PUT',
						url: SDS_APIGATEWAY_ENDPOINT,
						body: {
							updatedBy: userName!,
							snowEntity: {
								entityName: record.entityId,
								workflowType,
								status: 'READY',
							},
							requestId: uuidv4(),
						},
						credentials: credentials as any,
					});
				})
			);

			// Get entity names for selected entities only
			const selectedEntityNames = selectedEntities.map((record) => record.entityId);

			// Start workflow execution ONLY for selected entities
			onSuccessStartWorkflow(selectedEntityNames, batchExecutionRequired);
		} catch (err) {
			logger.error('Error updating entities to READY status:', err as Error);
		}
	};

	const {
		onClickCB: onWorkflowStartCB,
	} = useFetchOnUserActionUsingSDS<StartBulkWorkflowExecutionsResponse>();

	const startBulkWorkflow = async (
		entities: string[],
		batchExecutionRequired: boolean
	): Promise<StartBulkWorkflowExecutionsResponse | null> => {
		const response = await onWorkflowStartCB(
			{
				path: `/workflows/${workflowType}/bulk/execution`,
				method: 'POST',
				body: {
					entities,
					bulkExecutionId: bulkId,
					startedBy: userName,
					batchExecutionRequired,
					requestId: uuidv4(),
				},
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			},
			(data) => data
		) as StartBulkWorkflowExecutionsResponse | { error: string };

		if (!response || 'error' in response) {
			return null;
		}
		return response;
	};

	const handleBulkStartResponse = (response: StartBulkWorkflowExecutionsResponse, previousSucceededCount: number) => {
		const newSucceeded = Object.keys(response.entityToExecutionIdMap || {}).length;
		setSucceededCount(previousSucceededCount + newSucceeded);

		if (response.failedEntities && response.failedEntities.length > 0) {
			logger.warn('Partial failure in bulk workflow start', {
				workflowType,
				bulkExecutionId: bulkId,
				succeededCount: previousSucceededCount + newSucceeded,
				failedCount: response.failedEntities.length,
				failedEntities: response.failedEntities.map((e) => e.entityName),
			});
			setFailedEntities(response.failedEntities);
			setShowPartialFailureModal(true);
		} else if (previousSucceededCount + newSucceeded > 0) {
			setShowPartialFailureModal(false);
			onSuccessRedirectionCB();
		}
	};

	const onSuccessStartWorkflow = async (
		entities: string[],
		batchExecutionRequired: boolean
	) => {
		const response = await startBulkWorkflow(entities, batchExecutionRequired);
		if (response) {
			handleBulkStartResponse(response, 0);
		}
	};

	const retryFailedEntities = async () => {
		setIsRetrying(true);
		setRetryCount(retryCount + 1);
		const failedEntityNames = failedEntities
			.map((entity) => entity.entityName)
			.filter((name): name is string => !!name);
		logger.info('Retrying failed entities', {
			workflowType,
			bulkExecutionId: bulkId,
			entities: failedEntityNames,
			attempt: retryCount + 1,
		});
		const currentSucceeded = succeededCount;
		const response = await startBulkWorkflow(failedEntityNames, workflowDefinition?.batchExecutionRequired ?? false);
		setIsRetrying(false);

		if (response) {
			handleBulkStartResponse(response, currentSucceeded);
		}
	};

	useEffect(() => {
		if (authResponse) {
			onAuthCompletionCB({
				path: `/workflows/${workflowType}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			});
		}
	}, [authResponse]);

	const onSuccessRedirectionCB = () => {

		const pathName = `/entitySearch?workflowType=${workflowDefinition?.workflowId}&bulkExecutionId=${bulkId}`;
		const entityPageUrl = `${window.location.origin}${window.location.pathname}#${pathName}`;

		setRedirectUrl(entityPageUrl);
		timeOutRef.current = setTimeout(
			() => history.push(pathName),
			10000
		);
	};

	const isStepValid = () => {
		switch (activeStepIndex) {
		case 1:
			return selectedList.length > 0;
		default:
			return true;
		}
	};

	const handleNavigate = ({ detail }) => {
		if (detail.requestedStepIndex > activeStepIndex && !isStepValid()) {
			setShowValidationError(true);

			return;
		}

		setShowValidationError(false);
		setActiveStepIndex(detail.requestedStepIndex);
	};

	const {
		result: {
			isLoading: isWorkflowDataLoading,
			data: getWorkflowDefinitionData,
			error: workflowFetchError,
		},
		onClickCB: onAuthCompletionCB,
	} = useFetchOnUserActionUsingSDS<GetWorkflowDefinitionResponse>();

	const workflowDefinition: WorkflowDefinition | undefined = getWorkflowDefinitionData?.workflowDefinition;

	useEffect(() => {
	}, [workflowDefinition]);

	const steps = [
		{
			title: 'Download Template',
			content: <BulkExecutionDownloadTemplateStep
				workflowDefinition={workflowDefinition || {}}
			/>,
		},
		{
			title: 'Upload Requests',
			content: <BulkExecutionUploadInputStep list={selectedList}
				setList={setSelectedList}
				fetchedRecords={fetchedRecords}
				setFetchedRecords={setFetchedRecords}
				workflowDefinition={workflowDefinition}
				setBulkId={setBulkId}
				bulkId={bulkId}
				taskDefinition={getWorkflowDefinitionData?.taskDefinitions || []}
				userName={userName}
			/>,
			errorText: showValidationError ? 'Please select at least one entity to proceed.' : undefined,
		},
		{
			title: 'Review',
			content: <BulkExecutionReviewStep
				list={selectedList}
				workflowDefinition={workflowDefinition}
				bulkExecutionId={bulkId}
			/>,
		},
	];

	return (
		<>
			{redirectUrl && (
				<Modal visible={redirectUrl !== ''}>
					<Alert type="success">
						Workflow started successfully. Redirecting to the workflow execution
						page. Please click <a href={redirectUrl}> here </a> if you are not
						redirected with 10 seconds
					</Alert>
				</Modal>
			)}
			{
				workflowFetchError ? (
					<Alert type="error" header="Failed to load workflow">
						{workflowFetchError}
					</Alert>
				) : (!isWorkflowDataLoading && workflowDefinition) ? (
					<>
						<Wizard
							i18nStrings={{
								stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
								collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
								skipToButtonLabel: (step) => `Skip to ${step.title}`,
								navigationAriaLabel: 'Steps',
								previousButton: 'Back',
								nextButton: 'Next',
								cancelButton: 'Cancel',
								submitButton: 'Submit',
								optional: 'optional',
								nextButtonLoadingAnnouncement: (selectedList.length === 0) ? '' : 'Loading next step',
							}}
							steps={steps}
							activeStepIndex={activeStepIndex}
							onNavigate={handleNavigate}
							onSubmit={handleOnSubmit}
							onCancel={() => {
								setOnCancel(true);
							}}
						/>

						{(onCancel) && <Modal
							onDismiss={() => setOnCancel(false)}
							visible={onCancel}
							footer={
								<Box float="right">
									<Button
										variant="primary"
										onClick={() => {
											location.href = '../#/executeWorkflows';
										}}
									>
										Confirm
									</Button>
								</Box>
							}
							header="Confirmation"
						>
							Do you want to cancel the workflow execution progress?
						</Modal>}

						<Modal
							onDismiss={() => setShowLaunchModal(false)}
							visible={showLaunchModal}
							footer={
								<Box float="right">
									<Button
										variant="link"
										onClick={() => setShowLaunchModal(false)}
									>
										Cancel
									</Button>
									<Button
										variant="primary"
										onClick={async () => {
											await createEntityAndStartWorkflow(
												selectedList,
												workflowDefinition.batchExecutionRequired ?? false
											);
										}}
									>
										{'Launch Workflow'}
									</Button>
								</Box>
							}
							header="Confirmation"
						>
							By you starting the workflow, you hereby acknowledge and affirm
							that you have checked all the required inputs and to the fullest
							extent of your knowledge, meet the necessary standards for
							approval.
						</Modal>

						<Modal
							onDismiss={() => {
								setShowPartialFailureModal(false);
								if (succeededCount > 0) {
									onSuccessRedirectionCB();
								}
							}}
							visible={showPartialFailureModal}
							footer={
								<Box float="right">
									<SpaceBetween direction="horizontal" size="xs">
										<Button
											variant="normal"
											onClick={retryFailedEntities}
											loading={isRetrying}
											disabled={retryCount >= MAX_RETRIES}
										>
											{retryCount >= MAX_RETRIES ? 'Retry limit reached' : 'Retry Failed Entities'}
										</Button>
										<Button
											variant="primary"
											onClick={() => {
												setShowPartialFailureModal(false);
												if (succeededCount > 0) {
													onSuccessRedirectionCB();
												}
											}}
										>
											{succeededCount > 0 ? 'Continue' : 'Dismiss'}
										</Button>
									</SpaceBetween>
								</Box>
							}
							header={`${pluralizeEntity(failedEntities.length)} failed to start`}
						>
							<Alert type={succeededCount > 0 ? 'warning' : 'error'}>
								{succeededCount > 0
									? `Successfully started ${pluralizeEntity(succeededCount)}. The following could not be started:`
									: 'All entities failed to start:'}
							</Alert>
							<Box margin={{ top: 's' }}>
								{failedEntities.map((entity) => (
									<Box key={entity.entityName} margin={{ bottom: 'xs' }}>
										<strong>{entity.entityName}</strong>: {entity.reason}
									</Box>
								))}
							</Box>
						</Modal>
					</>
				) : (
					<ErrorLoader isLoading={true}></ErrorLoader>
				)
			}
		</>
	);
};

export default BulkUploadRequirementStepProgress;